import {Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Button} from 'primeng/button';
import {FormgridLabelComponent} from '../../../../shared/formgrid-label/formgrid-label.component';
import {Password} from 'primeng/password';
import {
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ViewService} from '../../../../shared/service/view.service';
import {PlayerProfileService} from '../../../service/player-profile.service';
import {RestErrorHelper} from '../../../../../helper/rest-error-helper';
import {AuthenticateResponse} from '../../../model/auth/authenticate-response';
import {InputText} from 'primeng/inputtext';
import {ActivatedRoute, Router} from '@angular/router';
import {Message} from 'primeng/message';
import {Divider} from 'primeng/divider';
import {ConfirmationService} from 'primeng/api';
import {InputOtp} from 'primeng/inputotp';
import {environment} from '../../../../../../environments/environment';
import {InfoTextComponent} from '../../../../shared/common/info-text/info-text.component';
import {InputGroup} from 'primeng/inputgroup';

enum PLAYER_LOGIN_VIEW {
  LOGIN, ENTER_REGISTRATIONCODE, LOGIN_PASSWORD
}

@Component({
  selector: 'app-player-profile-login-form',
  imports: [
    Button,
    FormgridLabelComponent,
    Password,
    ReactiveFormsModule,
    TranslatePipe,
    InputText,
    Message,
    Divider,
    FormsModule,
    InputOtp,
    InfoTextComponent,
    InputGroup
  ],
  templateUrl: './player-profile-login-form.component.html',
  styleUrl: './player-profile-login-form.component.scss'
})
export class PlayerProfileLoginFormComponent  implements OnInit
{
  E_LOGINVIEW = PLAYER_LOGIN_VIEW;

  public view = inject(ViewService);
  public playerProfileService = inject(PlayerProfileService);
  private restErrorHelper = inject(RestErrorHelper);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private confirmationService = inject(ConfirmationService);
  private activatedRoute = inject(ActivatedRoute);

  @ViewChild('inputUsername') inputUsername: ElementRef;
  @ViewChild('inputPassword') inputPassword: Password;

  @Input() minimal: boolean;
  @Input() showRegistration: boolean;
  @Input() noRedirect: boolean;
  @Output() onClosePopup = new EventEmitter<any>();
  @Output() onShowRegistration = new EventEmitter<any>();

  username: string;
  password: string;

  isSaving: boolean;
  isResettingPassword: boolean;
  response: AuthenticateResponse;
  errorMsgChangePassword: string;
  loginView: PLAYER_LOGIN_VIEW;
  usernameNotPlayerProfile: boolean;

  passwordMinLength = environment.minPasswordLengthPlayer;
  mailVerificationCode: string;
  forgotPassword1: string;
  forgotPassword2: string;

  ngOnInit(): void
  {
    if (this.playerProfileService.playerAuthenticationService.enterRegistrationCode)
      this.loginView = PLAYER_LOGIN_VIEW.ENTER_REGISTRATIONCODE;
    else
      this.loginView = PLAYER_LOGIN_VIEW.LOGIN;

    if (this.activatedRoute.snapshot.queryParamMap.has('userLogin'))
    {
      this.username = this.activatedRoute.snapshot.queryParamMap.get('userLogin');
      setTimeout(() => this.onUsernameNextClicked());
    }
    else this.username = this.playerProfileService?.playerAuthenticationService?.username;
  }

  public trimUsername(): void
  {
    if (this.username?.length > 0)
      this.username = this.username.trim();
    if (this.username.length === 4)
      this.username = this.username.toUpperCase();
  }

  public onUsernameNextClicked(): void
  {
    this.trimUsername();
    if (this.isSaving || !this.username)
      return;

    if (!(this.username.includes('@') || this.username.length === 4))
    {
      // Kein Spielerprofil Login -> Zum Backend Login verweisen
      this.usernameNotPlayerProfile = true;
    }
    else
    {
      this.usernameNotPlayerProfile = false;
      setTimeout(() => this.inputPassword?.input?.nativeElement?.focus());
    }

    this.errorMsgChangePassword = undefined;
    this.loginView = PLAYER_LOGIN_VIEW.LOGIN_PASSWORD;
  }

  public goto3KLogin(): void
  {
    this.router.navigate([environment.backendPrefix + '/login'], { queryParams: {userLogin: this.username}}).then(() => {});
  }

  public changeUsername(): void
  {
    this.errorMsgChangePassword = undefined;
    this.usernameNotPlayerProfile = false;
    this.loginView = PLAYER_LOGIN_VIEW.LOGIN;

    setTimeout(() =>
    {
      this.inputUsername?.nativeElement.select();
      this.inputUsername?.nativeElement.focus();
    });
  }

  public onOkClicked(): void
  {
    if (this.isSaving || !this.username || !this.password)
      return;

    this.isSaving = true;
    this.errorMsgChangePassword = undefined;

    const body = {
      mail: this.username,
      password: this.password
    };

    this.playerProfileService.login(body).subscribe({
      next: (response: AuthenticateResponse) =>
      {
        this.response = undefined;
        console.log('Anmeldeversuch erfolgt: ', response);
        this.isSaving = false;
        this.authenticateResponseReceived(response, body.mail);
      },
      error: error =>
      {
        this.response = undefined;
        this.isSaving = false;
        this.restErrorHelper.handleErrorService(error);
      }
    });
  }

  public onChangePasswordClicked(): void
  {
    if (this.isSaving || !this.mailVerificationCode || !this.forgotPassword1 || !this.forgotPassword2 || this.forgotPassword1 !== this.forgotPassword2)
      return;

    this.isSaving = true;
    const username = this.playerProfileService.playerAuthenticationService.registrationUsername;
    this.playerProfileService.passwordForgottenChange(username, this.forgotPassword1, this.mailVerificationCode).subscribe({
      next: (response: AuthenticateResponse) =>
      {
        console.log('Passwort geändert: ', response);
        this.isSaving = false;
        this.errorMsgChangePassword = undefined;
        this.playerProfileService.playerAuthenticationService.removeForgotPasswordData();
        this.loginView = PLAYER_LOGIN_VIEW.LOGIN;
        this.restErrorHelper.showInfoMessageService('PASSWORD_CHANGED_SUCCESS', environment.toastDurationShort);
      },
      error: error =>
      {
        this.isSaving = false;
        this.errorMsgChangePassword = undefined;
        if (error.message === 'MAIL_CONFIRMATION_CODE_NOT_CORRECT')
          this.errorMsgChangePassword = this.translate.instant('MAIL_CONFIRMATION_CODE_NOT_CORRECT', {value: error.attemptsLeft + ''});
        else this.restErrorHelper.handleErrorService(error);
      }
    });
  }

  private authenticateResponseReceived(response: AuthenticateResponse, username: string): void
  {
    if (response.errorMsg)
    {
      if (!username.includes('@') && username.length !== 4)
      {
        // Möglicherweise kein Spielerprofil Login -> Zum Backend Login verweisen
        this.router.navigate([environment.backendPrefix + '/login']).then(() => {});
      }
      else
      {
        // Meldung inline anzeigen + Anmeldeversuche anzeigen
        this.response = response;
      }
    }
    else
    {
      console.log('Spieler erfolgreich angemeldet', response);
      this.onClosePopup?.emit(true);
      if (!this.noRedirect || this.router.url.includes('/frontend/player/register'))
        this.playerProfileService.playerAuthenticationService.loginSuccessfulRoute();
    }
  }

  public resetPassword(): void
  {
    if (!this.username)
      return;

    this.confirmationService.confirm({
      message: this.translate.instant('RESET_PASSWORD_MSG',
        {value: this.username}),
      header: this.translate.instant('RESET_PASSWORD'),
      icon: 'pi pi-question-circle',
      acceptLabel: this.translate.instant('YES'),
      rejectLabel: this.translate.instant('NO'),
      rejectButtonStyleClass: 'button-no button-right',
      accept: () => this.resetPasswordService()
    });
  }

  private resetPasswordService(): void
  {
    const username = this.username;
    this.isResettingPassword = true;

    this.playerProfileService.passwordForgotten(username).subscribe({
      next: () =>
      {
        console.log('Passwort erfolgreich angefordert, Aktivierungscode zugesendet.');
        this.response = undefined;
        this.playerProfileService.playerAuthenticationService.enterRegistrationCode = true;
        this.playerProfileService.playerAuthenticationService.registrationUsername = username;
        this.loginView = PLAYER_LOGIN_VIEW.ENTER_REGISTRATIONCODE;

        this.isResettingPassword = false;
      },
      error: error =>
      {
        this.restErrorHelper.handleErrorService(error);
        this.isResettingPassword = false;
      }
    });
  }

  public backToLogin(): void
  {
    this.errorMsgChangePassword = undefined;
    this.playerProfileService.playerAuthenticationService.removeForgotPasswordData();
    this.loginView = PLAYER_LOGIN_VIEW.LOGIN;
  }

  /*public gotoRegistration(event: any): void
  {
    this.onClosePopup.emit(event);
    this.router.navigate([PLAYER_PREFIX_FULL + '/profile']).then(() => {});
  }*/

  public createAccount(): void
  {
    this.onShowRegistration.emit();
  }
}
